import { memo, useId } from 'react';

import { Spinner } from '@air/primitive-spinner';
import { cn } from '@air/utils-tailwind';

import blueStyles from '~/components/v2/components/workspace/onboarding-intro/walkthrough/onboardingIntroWalkthrough.module.css';
import { getAnimatedOnboardingUploadProgress } from '~/components/WorkspaceOnboardingForm/animatedOnboardingUploadProgress';

export type WorkspaceAnimatedOnboardingUploadProgressProps = {
  fileNames: readonly string[];
};

export const WorkspaceAnimatedOnboardingUploadProgress = memo(
  ({ fileNames }: WorkspaceAnimatedOnboardingUploadProgressProps) => {
    const headingId = useId();
    const uploadProgress = getAnimatedOnboardingUploadProgress(fileNames);

    return (
      <div className="flex flex-col items-center gap-6">
        <span
          className={cn(
            blueStyles.agentBlue,
            blueStyles.agentBlueDrift,
            'flex size-16 items-center justify-center rounded-full text-white',
          )}
          data-theme="light"
        >
          <Spinner className="size-8 text-white" />
        </span>
        <div aria-live="polite" className="flex flex-col items-center gap-3 text-center">
          <h1
            className="font-semibold ads:text-primary text-32 text-gray-12 leading-10"
            data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_HEADING"
            id={headingId}
          >
            {uploadProgress.heading}
          </h1>
          <p
            className="ads:text-secondary text-20 text-gray-11"
            data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_BODY"
          >
            They&apos;ll be on your board in a moment.
          </p>
          <ul className="flex flex-col items-center gap-1" data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_PROGRESS">
            {uploadProgress.names.map((name, index) => (
              <li
                className="max-w-md truncate ads:text-primary text-16 text-gray-12"
                data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_FILE_NAME"
                key={`${name}-${index}`}
              >
                {name}
              </li>
            ))}
            {uploadProgress.moreLabel ? (
              <li
                className="ads:text-secondary text-16 text-gray-11"
                data-testid="WORKSPACE_ANIMATED_ONBOARDING_UPLOAD_MORE"
              >
                {uploadProgress.moreLabel}
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    );
  },
);

WorkspaceAnimatedOnboardingUploadProgress.displayName = 'WorkspaceAnimatedOnboardingUploadProgress';
